import { Box, DialogProps, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import AddColumnDialog from '../../sidebar/columns/AddColumnDialog';
import { DataColumn, TagData } from '../../../types';
import TagSelectionInput from './TagSelectionInput';
import AggregationSelect from '../../sidebar/add-column-inputs/AggregationSelect';
import ColumnAggregations from '../../sidebar/add-column-inputs/ColumnAggregations';
import { AdvancedOptionsDataColumnReport } from '../../advanced-options-data-column-report/AdvancedOptionsDataColumnReport';
import { useFormContext } from 'react-hook-form';

type ColumnState = {
  tag?: TagData | null;
  aggregation?: string;
  unit?: string;
};

const AddDataColumnDialog: FunctionComponent<
  Omit<DialogProps, 'onSubmit'> & {
    dataColumn: DataColumn | null;
    dataColumnsList: DataColumn[];
    onSubmit: (data: ColumnState & { columnAggregation: Record<string, boolean> }) => void;
    onCancel: () => void;
    currentSelectedAggregationInterval: string;
  }
> = ({
  onSubmit,
  onCancel,
  dataColumnsList,
  dataColumn,
  currentSelectedAggregationInterval,
  ...props
}) => {
  const { watch } = useFormContext();
  const aggregationInterval = watch('data.aggregationInterval');

  const [columnState, setColumnState] = useState<ColumnState>({});
  const [columnStatistics, setColumnStatistics] = useState<Record<string, boolean>>(
    dataColumn?.columnStatistics || {
      TOTAL: false,
      MIN: false,
      MAX: false,
      AVERAGE: false,
    }
  );

  useEffect(() => {
    if (dataColumn) {
      setColumnState({
        tag: dataColumn.tag,
        aggregation: `${dataColumn?.aggregation?.value}_${dataColumn?.aggregation?.label}`,
      });
      setColumnStatistics(dataColumn.columnStatistics);
    } else {
      setColumnStatistics({
        TOTAL: false,
        MIN: false,
        MAX: false,
        AVERAGE: false,
      });
      setColumnState({});
    }
  }, [dataColumn, props.open, setColumnState]);

  const onChange = useCallback(
    (event: SelectChangeEvent<unknown>, name: string) => {
      setColumnState((prevState) => ({
        ...prevState,
        [name]: (event.target?.value || '') as string,
      }));
    },
    [setColumnState]
  );

  const onTagInputChange = useCallback((event: any) => onChange(event, 'aggregation'), [onChange]);
  let addColDialogDisabled = false;
  if (currentSelectedAggregationInterval === 'NONE') {
    addColDialogDisabled = !columnState.tag;
  } else {
    addColDialogDisabled = !columnState.tag || !columnState.aggregation;
  }

  return (
    <AddColumnDialog
      isEdit={!!dataColumn}
      disabled={addColDialogDisabled}
      onSubmit={() => onSubmit({ columnAggregation: columnStatistics, ...columnState })}
      onCancel={onCancel}
      columnStatistics={columnStatistics}
      {...props}
    >
      <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ maxHeight: '450px', overflowY: 'auto' }}>
          <TagSelectionInput
            value={dataColumn?.tag}
            onChange={(tag) => {
              setColumnState((prevState) => ({ ...prevState, tag: tag }));
            }}
          />
        </Box>

        <Box
          sx={{
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            border: '0.1px solid rgba(0,0,0,0.15)',
          }}
        >
          <Stack sx={{ padding: '12px', borderRadius: '12px' }}>
            {aggregationInterval !== 'NONE' && (
              <>
                <Typography fontWeight={600} sx={{ margin: '10px 0 15px 0' }}>
                  Aggregation
                </Typography>

                <AggregationSelect
                  value={columnState.aggregation ?? ''}
                  onChange={onTagInputChange}
                  disabled={currentSelectedAggregationInterval === 'NONE'}
                />
              </>
            )}

            <AdvancedOptionsDataColumnReport>
              <ColumnAggregations
                value={columnStatistics}
                onChange={(value) => setColumnStatistics(value)}
              />
            </AdvancedOptionsDataColumnReport>
          </Stack>
        </Box>
      </Stack>
    </AddColumnDialog>
  );
};

export default AddDataColumnDialog;
